import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { AssignedFeatureProfile, FeatureProfile } from '@app/core/interfaces/feature-profile';
import { FeatureProfileWithUsers } from '@app/core/interfaces/portal/feature-profile-with-users';
import { UpdateAssignedFeatureProfile } from '@app/core/interfaces/update-assigned-feature-profile';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeatureProfileRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  listResellerFeatureProfiles(
    withFeatures: boolean = false,
    withVirtualUserFeatureProfile: boolean = false
  ): Observable<FeatureProfile[]> {
    const params = new HttpParams().appendAll({ withFeatures, withVirtualUserFeatureProfile });
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.ServiceProvider}/featureProfiles`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/resellerFeatureProfiles`,
      })
      .pipe(
        mergeMap((url) => this.http.get<FeatureProfile[]>(url, { params })),
        wrapError('feature-profile-repository:list-reseller-feature-profiles')
      );
  }

  getResellerFeatureProfileById(id: number): Observable<FeatureProfile> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.ServiceProvider}/featureProfiles/${id}`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/resellerFeatureProfiles/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.get<FeatureProfile>(url)),
        wrapError(`feature-profile-repository:get-reseller-feature-profiles-by-id-${id}`)
      );
  }

  getActiveFeatureProfile(): Observable<FeatureProfile> {
    return this.utils.apiUrl(`/featureProfile`).pipe(
      mergeMap((url) => this.http.get<FeatureProfile>(url)),
      wrapError('feature-profile-repository:get-active-feature-profile')
    );
  }

  listAssignedFeatureProfiles(): Observable<AssignedFeatureProfile[]> {
    return this.utils.commonApiUrl('/featureProfile').pipe(
      mergeMap((url) => this.http.get<AssignedFeatureProfile[]>(url)),
      wrapError('feature-profile-repository:list-assigned-feature-profiles')
    );
  }

  listFeatureProfilesPerUser(): Observable<FeatureProfileWithUsers[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.Enterprise}/featureProfilesUsers`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/featureProfilesUsers`,
      })
      .pipe(
        mergeMap((url) => this.http.get<FeatureProfileWithUsers[]>(url)),
        wrapError('feature-profile-repository:list-feature-profiles-per-user')
      );
  }

  updateAssignedFeatureProfiles(profiles: UpdateAssignedFeatureProfile[]): Observable<void> {
    return this.utils.commonApiUrl('/assignedFeatureProfiles').pipe(
      mergeMap((url) => this.http.put<void>(url, profiles)),
      wrapError('feature-profile-repository:list-assigned-feature-profiles')
    );
  }
}
